import { useCallback, useMemo } from 'react';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { usePageContentId } from '@confluence/page-context';

import { usePermissionCheck } from '../../hooks/usePermissionCheck.tsx';
import {
	useGetModernizeChangeboardingOnboardingState,
	useSetModernizeChangeboardingOnboardingState,
	STEPS,
} from '../../hooks/useModernizeChangeboardingOnboardingState.ts';
import type { SegmentHook } from '../../hooks/useCombinedSegments.tsx';

import { Spotlights } from './Spotlights.tsx';

export const useLiveDocsSegment: SegmentHook = () => {
	const { loading, error, hasDismissedLiveDocTour, hasDismissedConvertToPageSpotlight } =
		useGetModernizeChangeboardingOnboardingState();
	const { completeSegments } = useSetModernizeChangeboardingOnboardingState();
	const isLivePage = useIsCurrentPageLive();
	const [contentId] = usePageContentId();
	const {
		canEdit,
		loading: contentQueryLoading,
		contentQueryError,
	} = usePermissionCheck(contentId);
	const showConvertToPageSpotlight = !hasDismissedConvertToPageSpotlight && canEdit;

	const steps = useMemo(
		() => [
			...(!hasDismissedLiveDocTour ? [Spotlights.ShareMenuSpotlight] : []),
			...(showConvertToPageSpotlight ? [Spotlights.ConvertToPageSpotlight] : []),
		],
		[hasDismissedLiveDocTour, showConvertToPageSpotlight],
	);

	const stepsToComplete = useMemo(() => {
		const keys = [];
		if (!hasDismissedLiveDocTour) {
			keys.push(STEPS.LIVE_DOC);
		}
		if (showConvertToPageSpotlight) {
			keys.push(STEPS.CONVERT_TO_PAGE);
		}
		return keys;
	}, [hasDismissedLiveDocTour, showConvertToPageSpotlight]);

	const onComplete = useCallback(() => {
		completeSegments(stepsToComplete);
	}, [stepsToComplete, completeSegments]);

	if (loading || contentQueryLoading) {
		return {
			steps: [],
			onComplete: () => {},
			loading: true,
		};
	}

	if (error || contentQueryError) {
		return {
			steps: [],
			onComplete: () => {},
			error: error || contentQueryError,
		};
	}

	if (!isLivePage) {
		return {
			steps: [],
			onComplete: () => {},
		};
	}

	return {
		stepsToComplete,
		steps,
		onComplete,
	};
};
